import { useCallback, useEffect, useRef, useState } from 'react';
import type { WalletPendingBreakdown, WalletPendingPayoutItem } from '@foodfluence/shared';
import { walletApi, WalletSessionResponse } from '../../../services/api/wallet';

export interface UseWalletBootstrapOptions {
  /** Influencer being viewed — sent as X-Influencer-ID for superuser account switching. */
  influencerId?: string;
  whopCompanyId?: string;
}

async function loadPendingBreakdown(influencerId: string): Promise<{
  pendingBreakdown: WalletPendingBreakdown;
  pendingItems: WalletPendingPayoutItem[];
}> {
  const data = await walletApi.getPendingBreakdown({ influencerId });
  return { pendingBreakdown: data.breakdown, pendingItems: data.items };
}

/**
 * Loads wallet on mount: sweeps pending payouts and returns Whop session for Elements.
 * KYC/withdraw token refresh is handled in WhopPayoutsModalProvider (GET /wallet/token, no bootstrap).
 * After a withdrawal, call refreshAvailableBalance (GET /wallet/balance).
 */
export function useWalletBootstrap({ influencerId, whopCompanyId }: UseWalletBootstrapOptions) {
  const [session, setSession] = useState<WalletSessionResponse | null>(null);
  const [kycApproved, setKycApproved] = useState<boolean | null>(null);
  const [transferredAmount, setTransferredAmount] = useState<number | undefined>(undefined);
  const [failedTransferCount, setFailedTransferCount] = useState(0);
  const [pendingBreakdown, setPendingBreakdown] = useState<WalletPendingBreakdown | undefined>(
    undefined,
  );
  const [pendingItems, setPendingItems] = useState<WalletPendingPayoutItem[] | undefined>(
    undefined,
  );
  const [pendingDataLoaded, setPendingDataLoaded] = useState(false);
  const [loading, setLoading] = useState(Boolean(whopCompanyId));
  const [balanceRefreshing, setBalanceRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Ignore stale getAccessToken / bootstrap KYC updates when a newer load started. */
  const kycUpdateSeqRef = useRef(0);

  const refreshAvailableBalance = useCallback(async () => {
    if (!whopCompanyId) return;
    setBalanceRefreshing(true);
    try {
      const { availableBalance } = await walletApi.getAvailableBalance({ influencerId });
      setSession((prev) => (prev ? { ...prev, availableBalance } : prev));
    } catch {
      // Keep the last known balance if refresh fails.
    } finally {
      setBalanceRefreshing(false);
    }
  }, [influencerId, whopCompanyId]);

  const handleKycApprovalChange = useCallback((approved: boolean) => {
    kycUpdateSeqRef.current += 1;
    setKycApproved(approved);
    setSession((prev) => (prev ? { ...prev, kycApproved: approved } : prev));
  }, []);

  /** Refresh KYC approval when the user returns to this tab (e.g. after verifying elsewhere). */
  useEffect(() => {
    if (!influencerId || !whopCompanyId) {
      return;
    }

    let cancelled = false;

    const refreshKycFromToken = async () => {
      const refreshSeq = ++kycUpdateSeqRef.current;
      try {
        const { kycApproved: approved } = await walletApi.getAccessToken({ influencerId });
        if (cancelled || refreshSeq !== kycUpdateSeqRef.current) return;
        setKycApproved(approved);
        setSession((prev) => (prev ? { ...prev, kycApproved: approved } : prev));
      } catch {
        // Ignore — keep last known status.
      }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void refreshKycFromToken();
      }
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', refreshKycFromToken);

    return () => {
      cancelled = true;
      kycUpdateSeqRef.current += 1;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', refreshKycFromToken);
    };
  }, [influencerId, whopCompanyId]);

  useEffect(() => {
    if (!influencerId) {
      setSession(null);
      setKycApproved(null);
      setTransferredAmount(undefined);
      setFailedTransferCount(0);
      setPendingBreakdown(undefined);
      setPendingItems(undefined);
      setLoading(false);
      setError(null);
      setPendingDataLoaded(true);
      return;
    }

    let cancelled = false;

    const applyPending = (pending: {
      pendingBreakdown: WalletPendingBreakdown;
      pendingItems: WalletPendingPayoutItem[];
    }) => {
      setPendingBreakdown(pending.pendingBreakdown);
      setPendingItems(pending.pendingItems);
      setPendingDataLoaded(true);
    };

    if (!whopCompanyId) {
      setSession(null);
      setKycApproved(null);
      setTransferredAmount(undefined);
      setFailedTransferCount(0);
      setPendingBreakdown(undefined);
      setPendingItems(undefined);
      setLoading(false);
      setError(null);
      setPendingDataLoaded(false);

      (async () => {
        try {
          const pending = await loadPendingBreakdown(influencerId);
          if (cancelled) return;
          applyPending(pending);
        } catch (err) {
          if (cancelled) return;
          setError(err instanceof Error ? err.message : 'Failed to load pending payouts');
          setPendingDataLoaded(true);
        }
      })();

      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    setError(null);
    setSession(null);
    setKycApproved(null);
    setTransferredAmount(undefined);
    setFailedTransferCount(0);
    setPendingBreakdown(undefined);
    setPendingItems(undefined);
    setPendingDataLoaded(false);
    const bootstrapSeq = ++kycUpdateSeqRef.current;

    (async () => {
      try {
        const data = await walletApi.bootstrap({ influencerId });
        if (cancelled || bootstrapSeq !== kycUpdateSeqRef.current) return;

        setKycApproved(data.kycApproved);
        setSession({
          token: data.token,
          companyId: data.companyId,
          availableBalance: data.availableBalance,
          kycApproved: data.kycApproved,
          ...(data.tokenExpiresAt && { tokenExpiresAt: data.tokenExpiresAt }),
          ...(data.requiresHttps && { requiresHttps: data.requiresHttps }),
        });
        if (data.transferredAmount > 0) {
          setTransferredAmount(data.transferredAmount);
        }
        if (data.failedTransferCount > 0) {
          setFailedTransferCount(data.failedTransferCount);
        }
        applyPending({
          pendingBreakdown: data.pendingBreakdown,
          pendingItems: data.pendingItems,
        });
      } catch (err) {
        if (cancelled) return;
        setSession(null);
        setKycApproved(null);
        setError(err instanceof Error ? err.message : 'Failed to load wallet');
        try {
          const pending = await loadPendingBreakdown(influencerId);
          if (cancelled) return;
          applyPending(pending);
        } catch {
          if (cancelled) return;
          setPendingDataLoaded(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      kycUpdateSeqRef.current += 1;
    };
  }, [influencerId, whopCompanyId]);

  return {
    session,
    kycApproved,
    transferredAmount,
    failedTransferCount,
    pendingBreakdown,
    pendingItems,
    pendingDataLoaded,
    loading,
    balanceRefreshing,
    error,
    refreshAvailableBalance,
    setKycApproved: handleKycApprovalChange,
  };
}
